import { Button, Form, message, Modal, Upload } from "antd";
import type { UploadFile } from "antd";
import { DeleteOutlined, DownloadOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import MESSAGES from "constant/messages";
import { useState } from "react";
import { StyledUpload } from "./styles";
import FilePreviewLink from "component/FilePreviewLink";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface IProps {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean
}

const isImageFile = (file: UploadFile) => file.type?.startsWith("image/");
const isVideoFile = (file: UploadFile) => file.type?.startsWith("video/");

const getFilePreviewUrl = (file: UploadFile) =>
  file.thumbUrl ??
  file.url ??
  (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "");

const FormItemFile: React.FC<IProps> = ({ name, label, required, disabled }) => {
  const [previewInfo, setPreviewInfo] = useState<{
    url: string;
    type: "image" | "video";
  } | null>(null);

  const itemRender = (
    originNode: React.ReactElement,
    file: UploadFile,
    _fileList: UploadFile[],
    actions: { download: () => void; preview: () => void; remove: () => void }
  ) => {
    let node = <span>{file.name}</span>;

    if (isVideoFile(file) || isImageFile(file)) {
      const src = getFilePreviewUrl(file);
      if (src) {
        node = (
          <span
            role="button"
            tabIndex={0}
            className="text-blue-500 cursor-pointer hover:underline"
            onClick={() =>
              setPreviewInfo({
                url: src,
                type: isImageFile(file) ? "image" : "video",
              })
            }
          >
            {file.name}
          </span>
        );
      }
    }
    const src = getFilePreviewUrl(file);
    return (
      <div className="flex justify-between items-center hover:bg-gray-300 px-1">
        <FilePreviewLink file={decodeURIComponent(src)} name={file.name} />

        <div className="flex gap-1">
          {
            !disabled && <span
              className="cursor-pointer"
              onClick={actions.remove}
              aria-label="Remove"
            >
              <DeleteOutlined />
            </span>
          }
        </div>
      </div>
    );
  };

  return (
    <>
      <Form.Item
        name={name}
        valuePropName="fileList"
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        rules={
          required ? [{ required: true, message: MESSAGES.requireField }] : []
        }
      >
        <StyledUpload
          maxCount={1}
          beforeUpload={(file) => {
            if (file.size && file.size > MAX_FILE_SIZE) {
              message.error(MESSAGES.invalidFileCapacity_10MB);
              return Upload.LIST_IGNORE;
            }
            return false;
          }}
          itemRender={itemRender}
        >
          <Button>
            <PlusOutlined /> <span>{label}</span>
          </Button>
        </StyledUpload>
      </Form.Item>

      <Modal
        title="プレビュー"
        open={!!previewInfo}
        onCancel={() => setPreviewInfo(null)}
        footer={null}
        destroyOnClose
        centered
      >
        {previewInfo && (
          <div className="flex justify-center items-center">
            {previewInfo.type === "image" && (
              <img src={previewInfo.url} alt="Preview" />
            )}
            {previewInfo.type === "video" && (
              <video src={previewInfo.url} controls preload="metadata" />
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default FormItemFile;
